import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Users, Plus, Trash2, Shield, User, Building, UserCog, ShieldCheck, UserMinus, Edit2, Ban, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Utenti = () => {
  const { user, isSindacatoAdmin, canManageDipendenti } = useAuth();
  const [users, setUsers] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [ruoliAzienda, setRuoliAzienda] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nome: '',
    cognome: '',
    ruolo_azienda_id: '',
    azienda_id: user?.azienda_id || ''
  });
  const [assignData, setAssignData] = useState({
    azienda_id: '',
    ruolo_azienda_id: ''
  });
  const [licenziaDialogOpen, setLicenziaDialogOpen] = useState(false);
  const [licenziaUser, setLicenziaUser] = useState(null);
  const [motivoLicenziamento, setMotivoLicenziamento] = useState('');
  
  // Dialog modifica dati utente
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    username: '',
    nome: '',
    cognome: ''
  });

  // Dialog ban utente
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banUser, setBanUser] = useState(null);
  const [banForm, setBanForm] = useState({
    motivo: '',
    durata_giorni: null,
    permanente: true
  });

  const isAdmin = isSindacatoAdmin();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [axios.get(`${API_URL}/users`)];
      if (isAdmin) {
        requests.push(axios.get(`${API_URL}/aziende`));
        requests.push(axios.get(`${API_URL}/ruoli-azienda`));
      } else if (user?.azienda_id) {
        requests.push(axios.get(`${API_URL}/ruoli-azienda?azienda_id=${user.azienda_id}`));
      }
      const responses = await Promise.all(requests);
      setUsers(responses[0].data);
      if (isAdmin) {
        setAziende(responses[1]?.data || []);
        setRuoliAzienda(responses[2]?.data || []);
      } else if (responses[1]) {
        setRuoliAzienda(responses[1].data || []);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const openEditUserDialog = (u) => {
    setEditingUser(u);
    setEditUserForm({
      username: u.username,
      nome: u.nome,
      cognome: u.cognome
    });
    setEditUserDialogOpen(true);
  };

  const handleEditUser = async () => {
    if (!editingUser) return;
    try {
      const updates = {};
      if (editUserForm.username !== editingUser.username) updates.username = editUserForm.username;
      if (editUserForm.nome !== editingUser.nome) updates.nome = editUserForm.nome;
      if (editUserForm.cognome !== editingUser.cognome) updates.cognome = editUserForm.cognome;
      
      if (Object.keys(updates).length === 0) {
        toast.info('Nessuna modifica da salvare');
        return;
      }
      
      await axios.patch(`${API_URL}/admin/users/${editingUser.id}/modifica`, updates);
      toast.success('Dati utente aggiornati');
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella modifica');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/users`, {
        ...formData,
        azienda_id: isAdmin ? (formData.azienda_id || null) : user?.azienda_id,
        ruolo_azienda_id: formData.ruolo_azienda_id || null
      });
      toast.success('Utente creato con successo');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleAssignRuolo = async () => {
    try {
      // Se c'è sia azienda che ruolo, usa l'endpoint di assunzione (supporta multi-azienda)
      if (assignData.azienda_id && assignData.azienda_id !== "none" && 
          assignData.ruolo_azienda_id && assignData.ruolo_azienda_id !== "none") {
        await axios.post(`${API_URL}/assunzioni?user_id=${selectedUser.id}&azienda_id=${assignData.azienda_id}&ruolo_azienda_id=${assignData.ruolo_azienda_id}`);
        toast.success('Impiego aggiunto con successo');
      } else {
        toast.error('Seleziona sia un\'azienda che un ruolo');
        return;
      }
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'assegnazione');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;
    try {
      await axios.delete(`${API_URL}/users/${id}`);
      toast.success('Utente eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const openLicenziaDialog = (u) => {
    setLicenziaUser(u);
    setMotivoLicenziamento('');
    setLicenziaDialogOpen(true);
  };

  const handleLicenzia = async () => {
    if (!licenziaUser) return;
    // Per admin usa l'azienda dell'utente da licenziare, per direttori usa la propria
    const aziendaId = isAdmin ? licenziaUser.azienda_id : user.azienda_id;
    if (!aziendaId) {
      toast.error('Utente non associato a nessuna azienda');
      return;
    }
    try {
      await axios.post(`${API_URL}/assunzioni/licenzia/${licenziaUser.id}/${aziendaId}`, {
        motivo: motivoLicenziamento || null
      });
      toast.success('Dipendente licenziato');
      setLicenziaDialogOpen(false);
      setLicenziaUser(null);
      setMotivoLicenziamento('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel licenziamento');
    }
  };

  const openAssignDialog = (u) => {
    setSelectedUser(u);
    setAssignData({
      azienda_id: u.azienda_id || '',
      ruolo_azienda_id: u.ruolo_azienda_id || ''
    });
    setAssignDialogOpen(true);
  };

  // Funzioni per il ban
  const canBanUsers = () => isAdmin || user?.permessi?.includes('gestisci_ban');
  
  const openBanDialog = (u) => {
    setBanUser(u);
    setBanForm({ motivo: '', durata_giorni: null, permanente: true });
    setBanDialogOpen(true);
  };

  const handleBan = async () => {
    if (!banForm.motivo.trim()) {
      toast.error('Inserisci un motivo per il ban');
      return;
    }
    try {
      await axios.post(`${API_URL}/ban/${banUser.id}`, {
        motivo: banForm.motivo,
        durata_giorni: banForm.permanente ? null : banForm.durata_giorni
      });
      toast.success(`${banUser.nome} ${banUser.cognome} è stato bannato`);
      setBanDialogOpen(false);
      setBanUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel ban');
    }
  };

  const handleUnban = async (userId) => {
    try {
      await axios.delete(`${API_URL}/ban/${userId}`);
      toast.success('Ban rimosso');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella rimozione del ban');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      nome: '',
      cognome: '',
      ruolo_azienda_id: '',
      azienda_id: user?.azienda_id || ''
    });
  };

  const getRuoloBadge = (u) => {
    if (u.ruolo === 'SINDACATO_ADMIN') {
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <Shield className="w-3 h-3 mr-1" />
          Admin Sindacato
        </Badge>
      );
    }
    if (u.ruolo === 'CITTADINO') {
      return (
        <Badge className="bg-green-100 text-green-800">
          <User className="w-3 h-3 mr-1" />
          Cittadino
        </Badge>
      );
    }
    if (u.ruolo_azienda_nome) {
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <ShieldCheck className="w-3 h-3 mr-1" />
          {u.ruolo_azienda_nome}
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-800">
        <UserCog className="w-3 h-3 mr-1" />
        Staff
      </Badge>
    );
  };

  // Filtra ruoli per l'azienda selezionata nel form
  const getRuoliPerAzienda = (aziendaId) => {
    if (!aziendaId) return [];
    return ruoliAzienda.filter(r => r.azienda_id === aziendaId);
  };

  return (
    <div data-testid="utenti-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Users className="w-8 h-8" />
            Gestione Utenti
          </h1>
          <p className="text-slate-600 mt-1">
            {isAdmin 
              ? 'Gestisci tutti gli utenti del sistema' 
              : `Gestisci i dipendenti di ${user?.azienda_nome}`}
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-btn" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Utente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Crea Nuovo Utente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    data-testid="user-nome-input"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cognome">Cognome</Label>
                  <Input
                    id="cognome"
                    data-testid="user-cognome-input"
                    value={formData.cognome}
                    onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  data-testid="user-username-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="user-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label>Azienda</Label>
                  <Select
                    value={formData.azienda_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, azienda_id: value === "none" ? "" : value, ruolo_azienda_id: '' })}
                  >
                    <SelectTrigger data-testid="user-azienda-select">
                      <SelectValue placeholder="Seleziona azienda" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna azienda (Cittadino)</SelectItem>
                      {aziende.map((az) => (
                        <SelectItem key={az.id} value={az.id}>{az.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {((formData.azienda_id && formData.azienda_id !== "none") || !isAdmin) && (
                <div className="space-y-2">
                  <Label>Ruolo Aziendale</Label>
                  <Select
                    value={formData.ruolo_azienda_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, ruolo_azienda_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="user-ruolo-select">
                      <SelectValue placeholder="Seleziona ruolo (opzionale)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun ruolo specifico</SelectItem>
                      {getRuoliPerAzienda(isAdmin ? formData.azienda_id : user?.azienda_id).map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" data-testid="submit-user-btn" className="bg-slate-900 hover:bg-slate-800">
                  Crea Utente
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : (
        <Card className="border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Utente</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Azienda</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={u.ban_info?.is_banned ? 'bg-red-50' : ''}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${u.ban_info?.is_banned ? 'bg-red-200' : 'bg-slate-200'}`}>
                        <span className={`text-xs font-semibold ${u.ban_info?.is_banned ? 'text-red-600' : 'text-slate-600'}`}>
                          {u.nome?.[0]}{u.cognome?.[0]}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>{u.nome} {u.cognome}</span>
                        {u.ban_info?.is_banned && (
                          <Badge className="bg-red-600 text-white text-xs">
                            <Ban className="w-3 h-3 mr-1" />
                            BANNATO
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{u.username}</TableCell>
                  <TableCell>{getRuoloBadge(u)}</TableCell>
                  <TableCell>
                    {u.impieghi && u.impieghi.length > 0 ? (
                      <div className="space-y-1">
                        {u.impieghi.map((imp, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 text-sm mr-2">
                            <Building className="w-3 h-3 text-slate-400" />
                            <span className="font-medium">{imp.azienda_nome}</span>
                            {imp.ruolo_nome && <span className="text-xs text-slate-500">({imp.ruolo_nome})</span>}
                          </span>
                        ))}
                      </div>
                    ) : u.azienda_nome ? (
                      <span className="inline-flex items-center gap-1 text-sm">
                        <Building className="w-3 h-3 text-slate-400" />
                        {u.azienda_nome}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {new Date(u.created_at).toLocaleDateString('it-IT')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Admin può modificare username/nome/cognome */}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-user-${u.id}`}
                          onClick={() => openEditUserDialog(u)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Modifica dati utente"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {(isAdmin || canManageDipendenti()) && u.ruolo !== 'SINDACATO_ADMIN' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`assign-user-${u.id}`}
                          onClick={() => openAssignDialog(u)}
                          title="Assegna ruolo"
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Solo Admin può eliminare, Direttori possono solo licenziare */}
                      {u.id !== user?.id && u.ruolo !== 'SINDACATO_ADMIN' && isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-user-${u.id}`}
                          onClick={() => handleDelete(u.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Elimina utente"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Pulsante Ban/Unban */}
                      {u.id !== user?.id && u.ruolo !== 'SINDACATO_ADMIN' && canBanUsers() && (
                        u.ban_info?.is_banned ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`unban-user-${u.id}`}
                            onClick={() => handleUnban(u.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Rimuovi ban"
                          >
                            <ShieldOff className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`ban-user-${u.id}`}
                            onClick={() => openBanDialog(u)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Banna utente"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )
                      )}
                      {/* Pulsante licenziamento con motivo - per Admin e Direttori */}
                      {u.id !== user?.id && u.ruolo !== 'SINDACATO_ADMIN' && u.azienda_id && (isAdmin || (canManageDipendenti() && u.azienda_id === user?.azienda_id)) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`licenzia-user-${u.id}`}
                          onClick={() => openLicenziaDialog(u)}
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          title="Licenzia dipendente"
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog Licenziamento con Motivo */}
      <Dialog open={licenziaDialogOpen} onOpenChange={setLicenziaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-red-600">Licenzia Dipendente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Stai per licenziare <strong>{licenziaUser?.nome} {licenziaUser?.cognome}</strong> da <strong>{licenziaUser?.azienda_nome || user?.azienda_nome}</strong>.
            </p>
            <div className="space-y-2">
              <Label>Motivo del Licenziamento (opzionale)</Label>
              <Textarea
                value={motivoLicenziamento}
                onChange={(e) => setMotivoLicenziamento(e.target.value)}
                placeholder="Inserisci il motivo del licenziamento..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setLicenziaDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleLicenzia} className="bg-red-600 hover:bg-red-700">
                <UserMinus className="w-4 h-4 mr-2" />
                Conferma Licenziamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Ban Utente */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-red-600 flex items-center gap-2">
              <Ban className="w-5 h-5" />
              Banna Utente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Stai per bannare <strong>{banUser?.nome} {banUser?.cognome}</strong> (@{banUser?.username})
            </p>
            <div className="space-y-2">
              <Label>Motivo del Ban *</Label>
              <Textarea
                value={banForm.motivo}
                onChange={(e) => setBanForm({ ...banForm, motivo: e.target.value })}
                placeholder="Inserisci il motivo del ban..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Durata</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={banForm.permanente}
                    onChange={() => setBanForm({ ...banForm, permanente: true, durata_giorni: null })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Permanente</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!banForm.permanente}
                    onChange={() => setBanForm({ ...banForm, permanente: false, durata_giorni: 7 })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Temporaneo</span>
                </label>
              </div>
            </div>
            {!banForm.permanente && (
              <div className="space-y-2">
                <Label>Giorni di ban</Label>
                <Input
                  type="number"
                  min="1"
                  value={banForm.durata_giorni || ''}
                  onChange={(e) => setBanForm({ ...banForm, durata_giorni: parseInt(e.target.value) || 1 })}
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setBanDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleBan} className="bg-red-600 hover:bg-red-700">
                <Ban className="w-4 h-4 mr-2" />
                Conferma Ban
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Ruolo Dialog - Per Admin Sindacato e Direttori */}
      {(isAdmin || canManageDipendenti()) && (
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Aggiungi Impiego</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Aggiungi un nuovo impiego a <strong>{selectedUser?.nome} {selectedUser?.cognome}</strong>
              </p>
              {/* Mostra impieghi attuali */}
              {selectedUser?.impieghi && selectedUser.impieghi.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs font-semibold text-blue-800 mb-2">Impieghi attuali:</p>
                  {selectedUser.impieghi.map((imp, idx) => (
                    <p key={idx} className="text-sm text-blue-700">
                      • {imp.azienda_nome} {imp.ruolo_nome && `(${imp.ruolo_nome})`}
                    </p>
                  ))}
                </div>
              )}
              {/* Solo Admin può cambiare azienda */}
              {isAdmin && (
                <div className="space-y-2">
                  <Label>Nuova Azienda</Label>
                  <Select
                    value={assignData.azienda_id || "none"}
                    onValueChange={(value) => setAssignData({ ...assignData, azienda_id: value === "none" ? "" : value, ruolo_azienda_id: '' })}
                  >
                    <SelectTrigger data-testid="assign-azienda-select">
                      <SelectValue placeholder="Seleziona azienda" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna azienda</SelectItem>
                      {aziende.map((az) => (
                        <SelectItem key={az.id} value={az.id}>{az.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Direttori vedono solo ruoli della propria azienda */}
              {!isAdmin && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <Building className="w-4 h-4 inline mr-1" />
                    Azienda: <strong>{user?.azienda_nome}</strong>
                  </p>
                </div>
              )}
              {((assignData.azienda_id && assignData.azienda_id !== "none") || !isAdmin) && (
                <div className="space-y-2">
                  <Label>Ruolo Aziendale</Label>
                  <Select
                    value={assignData.ruolo_azienda_id || "none"}
                    onValueChange={(value) => setAssignData({ ...assignData, ruolo_azienda_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="assign-ruolo-select">
                      <SelectValue placeholder="Seleziona ruolo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun ruolo specifico</SelectItem>
                      {getRuoliPerAzienda(isAdmin ? assignData.azienda_id : user?.azienda_id).map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>
                  Annulla
                </Button>
                <Button onClick={handleAssignRuolo} data-testid="confirm-assign-btn" className="bg-slate-900 hover:bg-slate-800">
                  Aggiungi Impiego
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Shield className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800">Sistema Permessi</h3>
              <ul className="mt-2 space-y-1 text-sm text-amber-700">
                <li><strong>Cittadino:</strong> Crea il proprio CV, si candida ai concorsi, legge la Gazzetta</li>
                <li><strong>Staff con Ruolo:</strong> I permessi dipendono dal ruolo aziendale assegnato</li>
                <li><strong>Admin Sindacato:</strong> Accesso completo, crea aziende e ruoli, pubblica Gazzetta</li>
              </ul>
              {isAdmin && (
                <p className="mt-3 text-sm text-amber-600">
                  💡 Puoi creare ruoli personalizzati per ogni azienda dalla pagina "Ruoli e Permessi"
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Modifica Utente */}
      {isAdmin && (
        <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <Edit2 className="w-5 h-5" />
                Modifica Dati Utente
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Username (Nick)</Label>
                <Input
                  value={editUserForm.username}
                  onChange={(e) => setEditUserForm({ ...editUserForm, username: e.target.value })}
                  placeholder="Username..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={editUserForm.nome}
                    onChange={(e) => setEditUserForm({ ...editUserForm, nome: e.target.value })}
                    placeholder="Nome..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome</Label>
                  <Input
                    value={editUserForm.cognome}
                    onChange={(e) => setEditUserForm({ ...editUserForm, cognome: e.target.value })}
                    placeholder="Cognome..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditUserDialogOpen(false)}>
                  Annulla
                </Button>
                <Button onClick={handleEditUser} className="bg-blue-600 hover:bg-blue-700">
                  Salva Modifiche
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Utenti;
